"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  Check,
  Loader2,
  Circle,
  XCircle,
  ListChecks,
} from "lucide-react";
import * as React from "react";

export type TaskStatus = "pending" | "in_progress" | "completed" | "error";

interface TaskContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const TaskContext = React.createContext<TaskContextValue | null>(null);

export interface TaskProps extends React.ComponentProps<"div"> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  status?: TaskStatus;
}

export function Task({
  children,
  className,
  open,
  defaultOpen = false,
  onOpenChange,
  status,
  ...props
}: TaskProps) {
  const [isOpen, setIsOpenState] = React.useState(defaultOpen);
  const actualOpen = open !== undefined ? open : isOpen;

  const setIsOpen = React.useCallback(
    (value: boolean) => {
      setIsOpenState(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );

  return (
    <TaskContext.Provider value={{ isOpen: actualOpen, setIsOpen }}>
      <Collapsible
        open={actualOpen}
        onOpenChange={setIsOpen}
        className={cn(
          "w-full rounded-xl border overflow-hidden",
          "bg-[color:var(--chat-agent-bubble-bg)] border-[color:var(--chat-agent-bubble-border)]",
          status === "in_progress" && "border-[color:var(--nt-primary)]/30",
          status === "error" && "border-[color:var(--icon-red)]/30",
          className,
        )}
        {...props}
      >
        {children}
      </Collapsible>
    </TaskContext.Provider>
  );
}

export type TaskTriggerProps = React.ComponentPropsWithoutRef<
  typeof CollapsibleTrigger
> & {
  title: string;
  status?: TaskStatus;
  completedCount?: number;
  totalCount?: number;
};

function StatusIcon({ status }: { status: TaskStatus }) {
  if (status === "completed") {
    return <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />;
  }
  if (status === "error") {
    return <XCircle className="h-4 w-4 text-[color:var(--icon-red)]" />;
  }
  if (status === "in_progress") {
    return (
      <Loader2 className="h-4 w-4 animate-spin text-[color:var(--nt-primary)]" />
    );
  }
  return <Circle className="h-3 w-3 text-[color:var(--foreground-muted)]" />;
}

export function TaskTrigger({
  className,
  title,
  status = "pending",
  completedCount,
  totalCount,
  ...props
}: TaskTriggerProps) {
  const counter =
    typeof completedCount === "number" && typeof totalCount === "number"
      ? `${completedCount}/${totalCount}`
      : null;

  return (
    <CollapsibleTrigger
      className={cn(
        "flex w-full items-center justify-between px-3.5 py-2.5 text-sm font-medium transition-colors group",
        "bg-[color:var(--chat-agent-bubble-bg)] hover:opacity-90",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2 min-w-0">
        <ChevronRight className="h-4 w-4 shrink-0 text-[color:var(--foreground-muted)] transition-transform duration-200 group-data-[state=open]:rotate-90" />
        <StatusIcon status={status} />
        <span className="truncate text-[13px] text-[color:var(--foreground)]">
          {title}
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {counter && (
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium text-[color:var(--foreground-muted)] border-[color:var(--card-border)]">
            <ListChecks className="h-3 w-3" />
            {counter}
          </span>
        )}
      </div>
    </CollapsibleTrigger>
  );
}

export function TaskContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  return (
    <CollapsibleContent
      className={cn(
        "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        className,
      )}
      {...props}
    >
      <div className="px-3.5 pb-3 pt-1.5">
        <ul className="space-y-2">{children}</ul>
      </div>
    </CollapsibleContent>
  );
}

export interface TaskItemProps extends React.ComponentProps<"li"> {
  status?: TaskStatus;
}

export function TaskItem({ className, status, children, ...props }: TaskItemProps) {
  return (
    <li
      className={cn(
        "flex items-start gap-2.5 text-[13px] text-[color:var(--foreground)]",
        className,
      )}
      {...props}
    >
      <span className="mt-[3px] shrink-0">
        {status ? <StatusIcon status={status} /> : <Circle className="h-3 w-3 text-[color:var(--foreground-muted)]" />}
      </span>
      <div className="min-w-0 flex-1 text-[color:var(--foreground-muted)]">
        {children}
      </div>
    </li>
  );
}

export function TaskItemFile({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5",
        "border-[color:var(--card-border)] bg-[color:var(--card-bg)]",
        "text-[11px] font-medium text-[color:var(--foreground)]",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}
